import { type WellLogsManagerWidgetInitParameters, WellLogsManagerWidgetState } from '@go-widgets/well-logs-widget';
import { action, comparer, makeObservable, observable, reaction } from 'mobx';

import { lagacyAgent } from 'src/api/legacyAgent';
import { wellLogsWidgetAgent } from 'src/api/wellLogsWidgetAgent';
import { requireService } from 'src/packages/di';
import { getNewAndDeletedFields } from 'src/packages/shared/utils/getNewAndDeletedElements';

import type { WellLogsManagerWidgetEntity } from './WellLogsManagerWidget.entity';
import type { GroupSelectStore } from '../group-select/GroupSelect.store';
import type { WellListPanelEntity } from '../well-list-panel/WellListPanel.entity';
import type { IReactionDisposer } from 'mobx';
import type { TGroup } from 'src/entities/tab/TabEntity';
import type { IWidgetStore } from 'src/entities/widget/IWidgetStore';
import type { WidgetEntity } from 'src/entities/widget/WidgetEntity';

import { setWellIdInGroup } from '../../utils/setWellIdInGroup';
import { WellListWidgetEntity } from '../well-list-widget/WellListWidget.entity';

import { getCorrectLocale } from './utils';

export class WellLogsManagerWidgetStore implements IWidgetStore {
  @observable isReady: boolean = false;
  @observable isGroupSelectOpen: boolean;
  @observable progress: number | null = null;
  @observable isExitFullscreenFetching: boolean = false;

  private readonly disposers: Map<WidgetEntity, IReactionDisposer> = new Map();
  private readonly wellLogsManagerEntity: WellLogsManagerWidgetEntity;
  private readonly groupSelectStore: GroupSelectStore;
  private readonly initParams: WellLogsManagerWidgetInitParameters;

  constructor(
    wellLogsManagerEntity: WellLogsManagerWidgetEntity,
    groupSelectStore: GroupSelectStore,
    private readonly wellListPanel: WellListPanelEntity,
    private readonly theme = requireService('theme'),
    private readonly stompCachePublisherService = requireService('stompCachePublisherService'),
    private readonly logStructureTemplatesService = requireService('logStructureTemplatesService'),
    private readonly language = requireService('language'),
    private readonly userPreferencesService = requireService('userPreference'),
    readonly classOfUnitsService = requireService('classOfUnitsService')
  ) {
    this.wellLogsManagerEntity = wellLogsManagerEntity;
    this.groupSelectStore = groupSelectStore;
    this.isGroupSelectOpen = wellLogsManagerEntity.isGroupsDefaultOpened;

    this.initParams = {
      axiosInstance: wellLogsWidgetAgent,
      legacyAxiosInstance: lagacyAgent,
      stompPublisher: this.stompCachePublisherService.stompPublisher,
      classOfUnitsService,
      supportEditing: true,
      logStructureTemplatesManager: this.logStructureTemplatesService,
    };

    makeObservable(this);
  }

  @action.bound
  setReady(value: boolean): void {
    this.isReady = value;
  }

  @action.bound
  setGroupSelectOpen(value: boolean): void {
    this.isGroupSelectOpen = value;
  }

  @action.bound
  setIsGroupSelectOpen(value: boolean): void {
    this.isGroupSelectOpen = value;
  }

  @action.bound
  setGroupForWellLogsManager(group: TGroup | null): void {
    this.wellLogsManagerEntity.setGroup(group ? group.id : null);
  }

  handleReloadWellStructure = (): void => {
    this.wellLogsManagerEntity.state?.reloadWellStructure();
  };

  private mapDeletedWidgets(widgets: WidgetEntity[]): void {
    for (const widget of widgets) {
      const dispose = this.disposers.get(widget);

      dispose?.();
      this.disposers.delete(widget);
    }
  }

  private mapNewWidgets(widgets: WidgetEntity[]): void {
    for (const widget of widgets) {
      if (!(widget instanceof WellListWidgetEntity) || this.disposers.has(widget)) {
        continue;
      }

      const disposeWellListWidget = reaction(
        () => widget.selectedWellID,
        (wellId) => {
          this.wellLogsManagerEntity.setWellId(wellId);
        }
      );

      this.disposers.set(widget, disposeWellListWidget);
    }
  }

  private getNewState(wellId: number) {
    return new WellLogsManagerWidgetState(
      this.initParams,
      {
        wellId,
        width: 520,
        height: 500,
        locale: getCorrectLocale(this.language.language),
        theme: this.theme.theme,
        isAltNames: this.userPreferencesService.preferences.isAltNames,
      },
      this.wellLogsManagerEntity.stateParams,
      {
        onInitializedSuccess: () => {
          this.setReady(true);
        },
        onInternalStateChanged: (state) => {
          this.wellLogsManagerEntity.setStateParams(state);
        },
      }
    );
  }

  effect = () => {
    const disposeGroupId = reaction(
      () => this.wellLogsManagerEntity.groupId,
      (groupId) => {
        if (groupId === null) return;

        const widgets = this.groupSelectStore.groupManager.getWidgetsByGroupId(groupId);
        setWellIdInGroup(widgets, this.wellLogsManagerEntity, this.wellListPanel);
      }
    );

    const dispose = reaction(
      () => this.groupSelectStore.groupManager.getWidgetsByGroupId(this.wellLogsManagerEntity.groupId).slice(),
      (widgets, prevState) => {
        const prevWidgets = prevState ?? [];

        const { newElements: newWidgets, deletedElements: deletedWidgets } = getNewAndDeletedFields<WidgetEntity>(
          widgets,
          prevWidgets
        );

        this.mapNewWidgets(newWidgets);
        this.mapDeletedWidgets(deletedWidgets);
      },
      { fireImmediately: true, equals: comparer.shallow }
    );

    const disposeWellId = reaction(
      () => this.wellLogsManagerEntity.wellId,
      (wellId, prevWellId) => {
        if (wellId && !this.wellLogsManagerEntity.state) {
          const newState = this.getNewState(wellId);
          this.wellLogsManagerEntity.setState(newState);
          this.setReady(true);
        } else if (wellId && this.wellLogsManagerEntity.state && typeof prevWellId !== 'undefined') {
          this.wellLogsManagerEntity.state.setAppParams({ wellId });
        } else if (!wellId && this.wellLogsManagerEntity.state) {
          this.wellLogsManagerEntity.state.destroy();
          this.wellLogsManagerEntity.setState(null);
          this.setReady(false);
        }
      },
      { fireImmediately: true }
    );

    const disposeStateParams = reaction(
      () => ({
        theme: this.theme.theme,
        language: this.language.language,
        isAltNames: this.userPreferencesService.preferences.isAltNames,
      }),
      ({ theme, language, isAltNames }) => {
        this.wellLogsManagerEntity.state?.setAppParams({
          theme,
          locale: getCorrectLocale(language),
          isAltNames,
        });
      },
      {
        fireImmediately: true,
      }
    );

    const wellListPanelDisposer = reaction(
      () => this.wellListPanel.selectedWellID,
      (wellId) => {
        if (this.wellListPanel.groupId === this.wellLogsManagerEntity.groupId) {
          this.wellLogsManagerEntity.setWellId(wellId);
        }
      },
      {
        fireImmediately: true,
      }
    );

    return () => {
      dispose();
      disposeGroupId();
      disposeWellId();
      disposeStateParams();
      this.mapDeletedWidgets(Array.from(this.disposers.keys()));
      wellListPanelDisposer();
    };
  };

  @action.bound
  exitFullScreen() {
    this.wellLogsManagerEntity.setFullscreen(false);
  }
}
